import { UseQueryResult } from '@tanstack/react-query';
import { isEmpty, noop, union, uniq } from 'lodash-es';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useFormContext } from 'react-hook-form';
import { Actions } from 'react-use/lib/useSet';
import { VenueMapContentProps } from 'src/components/Events/VenueMap/VenueMapProps';
import { SidePanelTab } from 'src/components/Listings/InventoryEventPage/MarketListings/MarketListingHeader';
import {
  ListingPricingInputWithId,
  useAutoPricingPreview,
} from 'src/components/Listings/MapWithCompListingsPreview/MapWithCompListingsPreview.hooks';
import { useListingGroupAutoPricePreview } from 'src/hooks/api/useListingGroupAutoPricePreview';
import { useComparableListingsVenueMapColor } from 'src/hooks/useComparableListingsVenueMapColor';
import { useGetCompListings } from 'src/hooks/useGetCompListings';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { useUserCanSetPrice } from 'src/hooks/useUserHasListingPermissions';
import { flattenListingGroup } from 'src/modals/GroupListings/components/groupingUtils';
import { getMatchingSectionRow } from 'src/utils/seatScoreUtils';
import {
  AutoPricingInputs,
  BulkListingPriceCalculationResult,
  CompListing,
  Event,
  Feature,
  Listing,
  ListingDetailsAutoPricingSectionUpdates,
  ListingGroup,
  ListingGroupAutoPriceSettings,
  ListingPriceCalculation,
  ListingPriceCalculationsInEventInput,
  SectionInfo,
  SectionRowScoreRecommendations,
  SelectedSectionSettings,
  VenueMapInfo,
} from 'src/WebApiController';

import { useAppContext } from '../AppContext';
import { useEventMapContext } from '../EventMapContext';
import { useLocalizationContext } from '../LocalizationContext';
import { usePricingSectionScoreRecommendations } from './InventoryEventDetailContext.hooks';
import { getListingPriceCalculationsInEventInputFromGroupSettings } from './InventoryEventDetailContext.utils';

export type IInventoryEventDetailContext = {
  event?: Event | undefined;
  eventListings?: Listing[] | null;
  selectedListingIds: number[];
  recommendedSectionIds?: number[]; // all recommending section ids
  recommendingSectionIds?: number[]; // section ids recommending but not taken
  selectedListing?: Listing | undefined;
  selectedGroup?: ListingGroup | undefined;
  flattenedListingGroup: Listing[] | undefined;
  selectedTab: SidePanelTab;
  onTabClick: (tab: SidePanelTab) => void;
  shouldRepaint: boolean;
  recenterMap: () => void;
  shouldReset: boolean;
  setShouldReset: React.Dispatch<React.SetStateAction<boolean>>;
  compQuantityFilters: string[];
  setCompQuantityFilters: React.Dispatch<React.SetStateAction<string[]>>;
  venueMapInfo: VenueMapInfo | null;
  priceCalcQuery: UseQueryResult<ListingPriceCalculation | null, Error> | null;
  listingGroupPriceCalcQuery: UseQueryResult<
    BulkListingPriceCalculationResult | null,
    Error
  > | null;
  compListings?: CompListing[] | undefined;
  filterSections: SectionInfo[];
  compListingSectionIds: number[];
  getColor: NonNullable<VenueMapContentProps['getColor']>;
  splitChoices: Record<string, string>;
  isLoading: boolean;
  sectionRowScoreRecommendations?: SectionRowScoreRecommendations;
  isRecommendationDeclined: boolean;
  onDeclineRecommendation: () => void;
  onToggleRecommendingSectionIds: (
    sectionIds: number[],
    options?: { include?: boolean; exclude?: boolean }
  ) => void;
};

const InventoryEventDetailContext = createContext<IInventoryEventDetailContext>(
  {
    event: undefined,
    eventListings: null,
    selectedListingIds: [],
    selectedListing: undefined,
    selectedGroup: undefined,
    flattenedListingGroup: undefined,
    selectedTab: SidePanelTab.LISTINGS,
    onTabClick: (tab: SidePanelTab) => {},
    shouldRepaint: false,
    recenterMap: () => {},
    shouldReset: false,
    setShouldReset: () => {},
    compQuantityFilters: [],
    setCompQuantityFilters: () => {},
    venueMapInfo: null,
    priceCalcQuery: null,
    listingGroupPriceCalcQuery: null,
    compListings: [],
    filterSections: [],
    compListingSectionIds: [],
    getColor: noop,
    splitChoices: {},
    isLoading: false,
    isRecommendationDeclined: false,
    onDeclineRecommendation: noop,
    onToggleRecommendingSectionIds: noop,
  }
);

export const useInventoryEventDetailContext = () =>
  useContext(InventoryEventDetailContext);

export const InventoryEventDetailContextProvider = <
  T extends
    | ListingDetailsAutoPricingSectionUpdates
    | ListingGroupAutoPriceSettings = ListingDetailsAutoPricingSectionUpdates,
>({
  event,
  eventListings,
  selectedListingIds,
  selectedListing,
  selectedGroup,
  children,
  sectionRowScoreRecommendations,
  isRecommendationDeclined = false,
  listingRecommendationDeclinedMutation,
}: {
  event: Event;
  eventListings?: Listing[] | null;
  selectedListingIds: number[];
  selectedListing: Listing | undefined;
  selectedGroup: ListingGroup | undefined;
  children: ReactNode;
  sectionRowScoreRecommendations?: SectionRowScoreRecommendations & {
    listingId: number;
  };
  isRecommendationDeclined?: boolean;
  listingRecommendationDeclinedMutation?: Actions<number>;
}) => {
  // Contexts
  const { venueMapInfo } = useEventMapContext();
  const { loginContext } = useAppContext();
  const { getUiCurrency } = useLocalizationContext();

  // State
  const [shouldRepaint, setShouldRepaint] = useState<boolean>(false);
  const [shouldReset, setShouldReset] = useState<boolean>(false);
  const [selectedTab, setSelectedTab] = useState<SidePanelTab>(
    SidePanelTab.LISTINGS
  );
  const [compQuantityFilters, setCompQuantityFilters] = useState<string[]>([]);

  const recenterMap = useCallback(() => {
    requestAnimationFrame(() => {
      setTimeout(() => {
        setShouldRepaint(true);

        setTimeout(() => {
          setShouldRepaint(false);
        }, 200);
      }, 50);
    });
  }, []);

  useEffect(() => {
    setShouldReset(true);
    requestAnimationFrame(() => {
      setTimeout(() => {
        setShouldReset(true);

        setTimeout(() => {
          setShouldReset(false);
        }, 200);
      }, 50);
    });
  }, [selectedListing?.id]);

  const uiCurrency = useMemo(
    () => getUiCurrency(loginContext?.user?.activeAccount?.currencyCode),
    [getUiCurrency, loginContext?.user?.activeAccount?.currencyCode]
  );

  const onTabClick = useCallback(
    (tab: SidePanelTab) => {
      setSelectedTab(tab);
      recenterMap();
    },
    [recenterMap]
  );

  const { watch, setValue, formState } = useFormContext<AutoPricingInputs>();

  const hasSelectedSectionScoreRecommendationsFeature = useUserHasFeature(
    Feature.SelectedSectionScoreRecommendations
  );

  const hasAutoPricingFeature = useUserHasFeature(Feature.AutoPricing);

  const { compListingSelectedSectionSettings } = watch();
  const input = watch();

  // Make sure the recommendation is for the selected listing
  const sanitizedScoreRecommendations = useMemo(():
    | SectionRowScoreRecommendations
    | undefined => {
    if (selectedListing == null || sectionRowScoreRecommendations == null) {
      return undefined;
    }

    const { listingId, ...res } = sectionRowScoreRecommendations;
    if (listingId !== selectedListing.id) {
      return undefined;
    }

    return res;
  }, [sectionRowScoreRecommendations, selectedListing]);

  const onUpdateSelectedSectionSettings = useCallback(
    (settings: SelectedSectionSettings | null) => {
      setValue('compListingSelectedSectionSettings', settings);
    },
    [setValue]
  );

  const {
    recommendingSectionIds,
    onDeclineRecommendation,
    onToggleRecommendingSectionIds,
  } = usePricingSectionScoreRecommendations(
    input.compListingSelectedSectionSettings,
    onUpdateSelectedSectionSettings,
    sanitizedScoreRecommendations?.recommendedSelectedSectionSettings,
    venueMapInfo?.sections ?? [],
    selectedListing,
    listingRecommendationDeclinedMutation,
    !(selectedListing?.id && hasSelectedSectionScoreRecommendationsFeature)
  );

  const flattenedListingGroup: Listing[] | undefined = useMemo(() => {
    if (!selectedGroup) {
      return;
    }

    return flattenListingGroup(selectedGroup).map((l) => l as Listing);
  }, [selectedGroup]);

  const sanitizedListingInput = useMemo(():
    | ListingPricingInputWithId
    | undefined => {
    if (selectedGroup || !input) {
      return undefined;
    }

    const {
      id,
      netProceedsFloor,
      netProceedsCeiling,
      autoPricingEnabled,
      currencyCode,

      compListingFloor,
      compListingCeiling,
      compListingMode,
      compListingOnlyForSameZoneEnabled,
      compListingOnlyForSelectedSectionsEnabled,
      compListingExcludeFanInventory,
      compListingExcludeDefects,
      compListingQuantityScoreAdjustmentEnabled,
      compListingQuantityScoreAdjustmentOverrideJson,
      compListingSelectedSectionSettings,
      compListingQuantityFilters,

      undercutMode,
      undercutAbsoluteAmount,
      undercutRelativeAmount,
      undercutReferenceListingPosition,

      circuitBreakerMaxDiscountVelocityPercent,
      circuitBreakerMaxDiscountVelocityTicksInHours,
      circuitBreakerMinCompListingCount,
      circuitBreakerRelativeCeiling,
      circuitBreakerRelativeFloor,

      outlierMode,
      outlierStandardDeviations,
      outlierKthLowestLimit,
      outlierKthLowestLimitRelativeSpacing,
      outlierKthLowestLimitAbsoluteSpacing,
    } = input as ListingDetailsAutoPricingSectionUpdates;

    const mergedInput = {
      id,
      netProceedsFloor,
      netProceedsCeiling,
      autoPricingEnabled,
      currencyCode,

      compListingFloor,
      compListingCeiling,
      compListingMode,
      compListingOnlyForSameZoneEnabled,
      compListingOnlyForSelectedSectionsEnabled,
      compListingExcludeFanInventory,
      compListingExcludeDefects,
      compListingQuantityScoreAdjustmentEnabled,
      compListingQuantityScoreAdjustmentOverrideJson,
      compListingSelectedSectionSettings,
      compListingQuantityFilters,

      undercutMode,
      undercutAbsoluteAmount,
      undercutRelativeAmount,
      undercutReferenceListingPosition,

      circuitBreakerMaxDiscountVelocityPercent,
      circuitBreakerMaxDiscountVelocityTicksInHours,
      circuitBreakerMinCompListingCount,
      circuitBreakerRelativeCeiling,
      circuitBreakerRelativeFloor,

      outlierMode,
      outlierStandardDeviations,
      outlierKthLowestLimit,
      outlierKthLowestLimitRelativeSpacing,
      outlierKthLowestLimitAbsoluteSpacing,
    } as ListingPricingInputWithId;

    return mergedInput;
  }, [selectedGroup, input]);

  // [NOTE]
  //
  // The below is masquerading as a ListingPriceCalculationsInEventInput object
  // because we don't have a true price preview endpoint that is specifically built
  // around the one pager listing group form.
  const autoPricingSettingsUpdates = useMemo(():
    | ListingPriceCalculationsInEventInput
    | undefined => {
    if (!selectedGroup || !formState.defaultValues) {
      return undefined;
    }

    return getListingPriceCalculationsInEventInputFromGroupSettings(
      input as ListingGroupAutoPriceSettings,
      formState.defaultValues,
      uiCurrency.code
    );
  }, [selectedGroup, formState.defaultValues, input, uiCurrency.code]);

  const { priceCalcQuery } = useAutoPricingPreview(
    event?.viagId ?? null,
    selectedListing?.id,
    sanitizedListingInput
  );

  const { listingCalculationsQuery: listingGroupPriceCalcQuery } =
    useListingGroupAutoPricePreview(
      event,
      flattenedListingGroup?.map((l) => l.id) ?? [],
      autoPricingSettingsUpdates
    );
  const { compListingsQuery, compListingsGroupQuery } = useGetCompListings(
    event,
    selectedListing?.id,
    selectedListing?.currency,
    selectedGroup?.ltGrpId
  );

  const formSectionFilterIds = useMemo(() => {
    return compListingSelectedSectionSettings?.sectionIdFilter ?? [];
  }, [compListingSelectedSectionSettings?.sectionIdFilter]);

  const compListings = useMemo(() => {
    if (selectedListing?.id && hasAutoPricingFeature) {
      return priceCalcQuery?.data?.compListings;
    }

    if (isEmpty(formSectionFilterIds)) {
      if (selectedGroup?.ltGrpId && hasAutoPricingFeature) {
        return (
          listingGroupPriceCalcQuery?.data?.compListings ||
          compListingsGroupQuery.data?.compListings
        );
      }
      return compListingsQuery.data ?? undefined;
    }

    if (selectedGroup?.ltGrpId && hasAutoPricingFeature) {
      const groupCompListings =
        listingGroupPriceCalcQuery?.data?.compListings ||
        compListingsGroupQuery.data?.compListings;

      return groupCompListings?.filter((l) => {
        const { section } = getMatchingSectionRow(l, venueMapInfo?.sections);
        return section && formSectionFilterIds.includes(section.id);
      });
    }

    return compListingsQuery.data?.filter((l) => {
      const { section } = getMatchingSectionRow(l, venueMapInfo?.sections);
      return section && formSectionFilterIds.includes(section.id);
    });
  }, [
    selectedListing?.id,
    hasAutoPricingFeature,
    formSectionFilterIds,
    selectedGroup?.ltGrpId,
    compListingsQuery.data,
    priceCalcQuery?.data?.compListings,
    listingGroupPriceCalcQuery?.data?.compListings,
    compListingsGroupQuery.data?.compListings,
    venueMapInfo?.sections,
  ]);

  const filterSections = useMemo(() => {
    const selectedSectionId =
      compListingSelectedSectionSettings?.sectionIdFilter ?? [];
    return (
      venueMapInfo?.sections?.filter(({ id }) =>
        selectedSectionId.includes(id)
      ) ?? []
    );
  }, [
    compListingSelectedSectionSettings?.sectionIdFilter,
    venueMapInfo?.sections,
  ]);

  // marked section ids in the venue map
  const compListingSectionIds = useMemo(() => {
    if (isEmpty(selectedListingIds)) {
      return formSectionFilterIds;
    }

    const sectionNames = (compListings ?? []).map((l) => l.section);
    const sectionIds = (venueMapInfo?.sections ?? [])
      .filter((section) => sectionNames.includes(section.name))
      .map(({ id }) => id);

    return union(sectionIds, formSectionFilterIds);
  }, [
    selectedListingIds,
    compListings,
    venueMapInfo?.sections,
    formSectionFilterIds,
  ]);

  const canSetPrice = useUserCanSetPrice(selectedListing, false);

  const compListingsFiltered = useMemo(() => {
    if (isEmpty(compQuantityFilters)) {
      return compListings;
    }

    return compListings?.filter(({ validPurchaseQuantities }) => {
      if (isEmpty(validPurchaseQuantities)) {
        return true;
      }
      return validPurchaseQuantities.some(
        (quantity) => compQuantityFilters.indexOf(quantity + '') > -1
      );
    });
  }, [compListings, compQuantityFilters]);

  const getColor = useComparableListingsVenueMapColor(
    eventListings ?? [],
    compListingsFiltered,
    compListings
  );

  const splitChoices = useMemo(() => {
    return uniq(
      (compListings ?? []).flatMap(
        (listing) => listing.validPurchaseQuantities ?? []
      )
    ).reduce(
      (res, quantity) => {
        const stringQuantity = quantity + '';
        res[stringQuantity] = stringQuantity;
        return res;
      },
      {} as Record<string, string>
    );
  }, [compListings]);

  const isLoading = useMemo(
    () =>
      compListingsQuery.isLoading ||
      priceCalcQuery.isLoading ||
      listingGroupPriceCalcQuery.isLoading ||
      compListingsGroupQuery.isLoading,
    [
      compListingsQuery.isLoading,
      priceCalcQuery.isLoading,
      listingGroupPriceCalcQuery.isLoading,
      compListingsGroupQuery.isLoading,
    ]
  );

  if (!canSetPrice) {
    return null;
  }

  return (
    <InventoryEventDetailContext.Provider
      value={{
        event,
        eventListings,
        selectedListingIds,
        recommendingSectionIds,
        selectedListing,
        selectedGroup,
        flattenedListingGroup,
        selectedTab,
        onTabClick,
        shouldRepaint,
        recenterMap,
        shouldReset,
        setShouldReset,
        compQuantityFilters,
        setCompQuantityFilters,
        venueMapInfo,
        priceCalcQuery,
        listingGroupPriceCalcQuery,
        compListings: compListingsFiltered,
        filterSections,
        compListingSectionIds,
        getColor,
        splitChoices,
        isLoading,
        sectionRowScoreRecommendations: sanitizedScoreRecommendations,
        isRecommendationDeclined,
        onDeclineRecommendation,
        onToggleRecommendingSectionIds,
      }}
    >
      {children}
    </InventoryEventDetailContext.Provider>
  );
};
